import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import { NotificationAdd, Lock } from '@mui/icons-material'
import _cloneDeep from 'lodash.clonedeep'
import { useModalContext } from '../../contexts/ModalProvider'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import flattenPattern from '../../common/utils/flatternPattern'
import normalizeTradeSettingsForAlert, {
  normalizeAccountSettingsForAlert,
} from '../../common/utils/normalizeTradeSettingsForAlert'
import { Pattern } from '../../app/builder/contexts/PatternContext'
import { normalizeSchedule, convertScheduleTz } from '../../common/backtestSchedule'
import { getClientTimeZone } from '../../common/timezones'

interface CreateAlertActionButtonProps {
  onClick?: () => void
  backtest?: any
  pattern?: Pattern | null
  asButton?: boolean
  disabled?: boolean
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  [otherProps: string]: any
}

function CreateAlertActionButton({
  onClick,
  backtest,
  pattern,
  asButton = false,
  disabled = false,
  color = 'primary',
  ...otherProps
}: CreateAlertActionButtonProps) {
  const { canCreateAlerts } = useApplicationSuiteContext()
  const { setIsCreateAlertModalOpen, setDefaultAlert } = useModalContext()
  const [isHovered, setIsHovered] = useState(false)

  function openAlertModal() {
    if (pattern) {
    }

    if (pattern || backtest) {
      // Use pattern only if it's not null
      const _pattern: Pattern = backtest
        ? _cloneDeep(backtest.pattern)
        : pattern
          ? _cloneDeep(pattern)
          : ({} as Pattern)

      let alertName = `${_pattern?.name || 'Unnamed Pattern'}`

      if (backtest) {
        const conditions = flattenPattern(_pattern)
        for (const condition of conditions) {
          // Ensure chartDetails exists before assigning
          if (condition.chartDetails) {
            condition.chartDetails.symbol = backtest.backtestSettings.executionSymbol
          } else {
            console.warn('Condition missing chartDetails', condition)
          }
        }
        // Ensure trigger and chartDetails exist
        if (_pattern.trigger?.chartDetails) {
          _pattern.trigger.chartDetails.symbol = backtest.backtestSettings.executionSymbol
        } else {
          console.warn('Pattern trigger or trigger.chartDetails missing')
        }

        alertName += ` - ${backtest.backtestSettings.executionSymbol}`
      }

      setDefaultAlert((prev) => ({
        ...prev,
        pattern: _pattern,
        name: `${alertName} - Alert`,
        ...(backtest && {
          symbol: backtest.backtestSettings.executionSymbol,
          from: 'backtest',
          backtestId: backtest.id,
          backtestGcid: backtest.gcid,
          schedule: convertScheduleTz(
            normalizeSchedule(backtest.backtestSettings),
            getClientTimeZone(),
            backtest.backtestSettings.dateRange?.startDate
          ),
          ...(backtest.backtestSettings.generateTrades &&
            backtest.backtestSettings.analyzeTrades && {
              trading: {
                ...prev.trading,
                options: {
                  generateTrades: normalizeTradeSettingsForAlert(
                    _cloneDeep(backtest.backtestSettings.generateTrades)
                  ),
                  analyzeTrades: normalizeAccountSettingsForAlert(
                    _cloneDeep(backtest.backtestSettings.analyzeTrades)
                  ),
                },
              },
            }),
        }),
        ...(pattern && {
          from: 'pattern',
        }),
      }))
    }

    setIsCreateAlertModalOpen(true)

    if (onClick) {
      onClick()
    }
  }

  const isDisabled = !canCreateAlerts || disabled

  const buttonContent =
    !canCreateAlerts && isHovered ? <Lock sx={{ color: 'warning.main' }} /> : <NotificationAdd />

  // Create a common props object for Button/IconButton
  const commonProps = {
    onClick: canCreateAlerts ? openAlertModal : undefined,
    disabled: isDisabled,
    ...otherProps, // Spread other props like sx, aria-label etc.
  }

  // Determine the button component based on asButton prop
  const buttonElement = asButton ? (
    <Button
      variant="contained"
      size="small"
      startIcon={buttonContent}
      color={color}
      sx={{ flexShrink: 0 }}
      {...commonProps}
      data-tour-id="create-alert-button"
    >
      Create Alert
    </Button>
  ) : (
    <IconButton
      color="inherit" // Default color for IconButton, can be overridden by ...otherProps or sx
      {...commonProps}
    >
      {buttonContent}
    </IconButton>
  )

  // Tooltip content varies based on the mode
  let tooltipTitle = 'Create Alert'
  if ((backtest || pattern) && canCreateAlerts) {
    tooltipTitle = `Create alert from ${backtest ? 'backtest' : 'pattern'}`
  }

  // Render logic for disabled state (Upgrade tooltip)
  if (!canCreateAlerts) {
    return (
      <Tooltip
        title="Upgrade to create alerts"
        placement="top"
        // Only show tooltip when hovered to avoid showing it constantly when disabled
        // disableHoverListener={!isHovered} // This might feel odd, better to always show on hover if disabled
        // disableFocusListener
        // disableTouchListener
      >
        {/* Box is needed for Tooltip to work on disabled elements */}
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{ display: 'inline-block', cursor: 'not-allowed' }} // Use not-allowed cursor
        >
          {/* Render the button with disabled styles applied correctly */}
          {React.cloneElement(buttonElement, { disabled: true })}
        </Box>
      </Tooltip>
    )
  }

  // Render logic for enabled state (Standard tooltip)
  return (
    <Tooltip title={tooltipTitle} placement="top">
      {/* No extra Box needed here as the button is enabled */}
      {buttonElement}
    </Tooltip>
  )
}

export default CreateAlertActionButton
